import React, { useEffect, useState, useCallback } from "react";
import { api, extractApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Zap, Package, CheckCircle2, Plus, X, Sparkles, Ban } from "lucide-react";
import { useLanguage } from "@/lib/LanguageContext";
import { pickLocalized } from "@/lib/i18n";
import { billingErrorMessage } from "../../lib/billingErrors";
// Relative on purpose: no "@" alias under Jest.
import { useEntitlements } from "../entitlements/EntitlementsContext";
import useBoosterCheckout from "./useBoosterCheckout";

const CUR = { CAD: "$", USD: "$", EUR: "€", INR: "₹" };

/**
 * `status: "blocked"` from the purchase-status read: Stripe took the money but the
 * server refused to activate the booster (its capability is not in the plan, not built,
 * or the booster was withdrawn), and the refund is manual (`refund: "manual"`). Reason
 * per code; an unknown code still gets the refusal and the refund, never the raw code.
 */
const BLOCKED_REASON_KEYS = {
  CAPABILITY_NOT_IN_PLAN: "bill.blocked.reason.notInPlan",
  CAPABILITY_NOT_BUILT: "bill.blocked.reason.notBuilt",
  BOOSTER_UNAVAILABLE: "bill.blocked.reason.unavailable",
};
const blockedMessage = (code, t) => {
  const reasonKey = BLOCKED_REASON_KEYS[code];
  return reasonKey
    ? t("bill.blocked.withReason", { reason: t(reasonKey) })
    : t("bill.blocked.noReason");
};

/**
 * PurchaseCatalogCard — achat de Boosters (abonnement récurrent) + Packages/Porting (paiement unique).
 * Paiement Stripe réel via la clé plateforme Solunary. Gère le retour ?purchase=success&session_id=.
 */
export const PurchaseCatalogCard = ({ onPurchased, t }) => {
  const { lang } = useLanguage();
  const ent = useEntitlements();
  const [catalog, setCatalog] = useState(null);
  const [busy, setBusy] = useState(null);
  // Capability key → label, from the public capability catalogue (the one authority for
  // capability copy). Read only when a booster is unavailable for want of one; a failed
  // read degrades to the generic "not available" sentence, never to a raw key.
  const [capLabels, setCapLabels] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/tenant/billing/catalog");
      setCatalog(data);
      const needsLabel = (data?.boosters || []).some((b) => b.available === false && b.requires_capability);
      if (needsLabel) {
        api.get("/saas/capabilities")
          .then(({ data: cat }) => setCapLabels(Object.fromEntries(
            (cat?.capabilities || []).map((c) => [c.key, c]),
          )))
          .catch(() => setCapLabels({}));
      }
    } catch (e) {
      toast.error(extractApiError(e, t("bill.error")));
    }
  }, [t]);

  /**
   * Why an unavailable booster cannot be subscribed. `available: false` is the server's
   * verdict; the reason is read from the practice's capability map: the capability is not
   * built → "Coming soon"; otherwise → "Requires <label>". No capability named, or its
   * label unreadable → the generic sentence.
   */
  const unavailableReason = (b) => {
    const key = b.requires_capability;
    if (key && ent.status(key) === "coming_soon") return t("bill.boosterComingSoon");
    const row = key ? capLabels[key] : null;
    if (!row) return t("bill.boosterNotAvailable");
    const label = pickLocalized(
      { label_en: row.label, label_fr: row.label_i18n?.fr, label_es: row.label_i18n?.es },
      "label",
      lang,
    );
    return label ? t("bill.boosterRequires", { capability: label }) : t("bill.boosterNotAvailable");
  };

  useEffect(() => { load(); }, [load]);

  // Retour depuis Stripe Checkout — the read+strip is `useBoosterCheckout`'s (shared with
  // Promote, C3.04); only what "activated" MEANS for this card (reload the catalogue,
  // tell the parent) stays here.
  const handleBoosterReturn = useCallback((data) => {
    if (data.status === "blocked") {
      toast.error(blockedMessage(data.code, t));
      load();
    } else if (data.status === "completed") {
      toast.success(data.kind === "booster" ? t("bill.boosterActivated") : t("bill.purchaseConfirmed"));
      load();
      onPurchased?.();
    } else if (data.status === "cancelled") {
      toast.info(t("bill.purchaseCancelled"));
    }
  }, [t, load, onPurchased]);
  const { purchase: purchaseBooster, busy: checkoutBusy } = useBoosterCheckout({ onReturn: handleBoosterReturn });
  const isBusy = (id) => busy === id || checkoutBusy === id;

  const fmt = (price, cur) => `${CUR[cur] || ""}${Number(price).toFixed(2)} ${cur}`;

  const subscribeBooster = async (b) => {
    try {
      const data = await purchaseBooster(b.booster_id);
      if (!data.checkout_url) {
        // Demo mode: activated synchronously, no Stripe redirect to return from.
        toast.success(t("bill.boosterActivated"));
        await load();
        onPurchased?.();
      }
    } catch (e) {
      toast.error(billingErrorMessage(e, t, t("bill.error")));
    }
  };

  const cancelBooster = async (b) => {
    if (!window.confirm(t("bill.confirmCancelBooster", { name: b.name }))) return;
    setBusy(b.booster_id);
    try {
      await api.delete(`/tenant/billing/booster/${b.booster_id}`);
      toast.success(t("bill.boosterDeactivated"));
      await load();
      onPurchased?.();
    } catch (e) {
      toast.error(extractApiError(e, t("bill.error")));
    } finally {
      setBusy(null);
    }
  };

  const buySku = async (s) => {
    setBusy(s.sku_id);
    try {
      const { data } = await api.post("/tenant/billing/sku/checkout", { sku_id: s.sku_id, origin_url: window.location.origin });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.success(t("bill.purchaseConfirmed"));
      await load();
      onPurchased?.();
    } catch (e) {
      toast.error(billingErrorMessage(e, t, t("bill.error")));
    } finally {
      setBusy(null);
    }
  };

  if (!catalog) return <div className="bg-white rounded-2xl border border-eh-border-soft p-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-eh-muted" /></div>;

  const installSkus = catalog.skus.filter((s) => s.category !== "data_porting");
  const portingSkus = catalog.skus.filter((s) => s.category === "data_porting");

  return (
    <div className="space-y-6" data-testid="purchase-catalog">
      {/* Boosters */}
      <div className="bg-white rounded-2xl border border-eh-border-soft p-6">
        <h3 className="font-display text-xl flex items-center gap-2 mb-1"><Zap className="w-5 h-5 text-sol-gold" /> {t("bill.boosters")}</h3>
        <p className="text-eh-muted text-sm mb-4">{t("bill.boostersSub")}</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {catalog.boosters.map((b) => {
            // An ACTIVE booster whose capability is no longer built is still billed until
            // it is removed: shown without the green "Active", with the retirement note
            // (the PaidAddonsCard treatment), and still deactivatable. `available: false`
            // on an INACTIVE booster withholds Subscribe and says why.
            const retired = b.active && b.active_retired === true;
            const unavailable = !b.active && b.available === false;
            return (
              <div key={b.booster_id} className={`rounded-xl border p-4 ${b.active && !retired ? "border-emerald-300 bg-emerald-50/40" : "border-eh-border-soft"}`} data-testid={`booster-${b.booster_id}`}>
                <div className="flex items-center justify-between">
                  <div className="font-medium text-eh-ink">{b.name}</div>
                  <div className="text-sm text-eh-muted">{fmt(b.price, b.currency)}{t("bill.perMonth")}</div>
                </div>
                {b.description && <p className="text-xs text-eh-muted mt-1">{b.description}</p>}
                <div className="mt-3">
                  {b.active ? (
                    <Button size="sm" variant="outline" onClick={() => cancelBooster(b)} disabled={busy === b.booster_id} data-testid={`cancel-booster-${b.booster_id}`}>
                      {busy === b.booster_id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <X className="w-3 h-3 mr-1" />} {t("bill.deactivate")}
                    </Button>
                  ) : unavailable ? (
                    <span className="text-xs text-eh-muted inline-flex items-center gap-1" data-testid={`booster-unavailable-${b.booster_id}`}>
                      {unavailableReason(b)}
                    </span>
                  ) : (
                    <Button size="sm" onClick={() => subscribeBooster(b)} disabled={isBusy(b.booster_id)} data-testid={`subscribe-booster-${b.booster_id}`} className="bg-sol-gold text-eh-ink hover:bg-sol-gold/90">
                      {isBusy(b.booster_id) ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />} {t("bill.subscribe")}
                    </Button>
                  )}
                  {b.active && !retired && <span className="ml-2 text-xs text-emerald-700 inline-flex items-center gap-1" data-testid={`booster-active-${b.booster_id}`}><CheckCircle2 className="w-3 h-3" /> {t("bill.active")}</span>}
                  {retired && (
                    <p className="mt-2 text-xs text-eh-muted flex items-start gap-1" data-testid={`booster-retired-${b.booster_id}`}>
                      <Ban className="w-3 h-3 mt-0.5 shrink-0" aria-hidden="true" />
                      <span>{t("bill.addonRetired")}</span>
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Packages / Installs */}
      <div className="bg-white rounded-2xl border border-eh-border-soft p-6">
        <h3 className="font-display text-xl flex items-center gap-2 mb-1"><Package className="w-5 h-5 text-eh-sage-deep" /> {t("bill.packages")}</h3>
        <p className="text-eh-muted text-sm mb-4">{t("bill.packagesSub")}</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {installSkus.map((s) => <SkuCard key={s.sku_id} s={s} busy={busy} onBuy={buySku} fmt={fmt} t={t} />)}
        </div>
      </div>

      {/* Data porting */}
      {portingSkus.length > 0 && (
        <div className="bg-white rounded-2xl border border-eh-border-soft p-6">
          <h3 className="font-display text-xl flex items-center gap-2 mb-1"><Sparkles className="w-5 h-5 text-sol-gold" /> {t("bill.porting")}</h3>
          <p className="text-eh-muted text-sm mb-4">{t("bill.portingSub")}</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {portingSkus.map((s) => <SkuCard key={s.sku_id} s={s} busy={busy} onBuy={buySku} fmt={fmt} t={t} />)}
          </div>
        </div>
      )}
    </div>
  );
};

const SkuCard = ({ s, busy, onBuy, fmt, t }) => (
  <div className={`rounded-xl border p-4 ${s.purchased ? "border-emerald-300 bg-emerald-50/40" : "border-eh-border-soft"}`} data-testid={`sku-${s.sku_id}`}>
    <div className="flex items-center justify-between">
      <div className="font-medium text-eh-ink">{s.name}</div>
      <div className="text-sm text-eh-muted">{s.is_starting_at ? t("bill.startingAt") : ""}{fmt(s.price, s.currency)}</div>
    </div>
    {s.tagline && <p className="text-xs text-eh-muted mt-1">{s.tagline}</p>}
    <div className="mt-3">
      {s.purchased ? (
        <span className="text-xs text-emerald-700 inline-flex items-center gap-1" data-testid={`sku-owned-${s.sku_id}`}><CheckCircle2 className="w-3 h-3" /> {t("bill.owned")}</span>
      ) : (
        <Button size="sm" onClick={() => onBuy(s)} disabled={busy === s.sku_id} data-testid={`buy-sku-${s.sku_id}`} className="bg-eh-ink text-eh-bg">
          {busy === s.sku_id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />} {t("bill.buy")}
        </Button>
      )}
    </div>
  </div>
);

export default PurchaseCatalogCard;
